import React from 'react';
import { Edit3, Trash2 } from 'lucide-react';

export const PaymentSummary = ({ payments, onEdit, onDelete, onWhatsApp, customerPhone }) => {
  // Calculate totals
  const cashPayments = payments.filter(p => ['Cash', 'Card', 'UPI', 'IMPS', 'NEFT/RTGS', 'Cheque'].includes(p.mode));
  const oldGoldPayments = payments.filter(p => p.mode === 'Old Gold');
  const metalCredits = payments.filter(p => p.mode === 'Metal Credit');
  
  const totalCash = cashPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalOldGold = oldGoldPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalOldGoldFine = oldGoldPayments.reduce((sum, p) => sum + (p.fine_grams || 0), 0);
  const totalMetalCredit = metalCredits.reduce((sum, p) => sum + (p.fine_wt || 0), 0);

  const getModeIcon = (mode) => {
    const icons = {
      'Cash': '💵',
      'Card': '💳',
      'UPI': '📱',
      'IMPS': '🏦',
      'NEFT/RTGS': '🏦',
      'Cheque': '📄',
      'Old Gold': '🪙',
      'Metal Credit': '✨'
    };
    return icons[mode] || '💰';
  };

  const getPaymentDetails = (payment) => {
    const parts = [];
    
    // Reference info
    if (payment.upi_ref) parts.push(`UPI: ...${payment.upi_ref.slice(-4)}`);
    if (payment.utr_ref) parts.push(`UTR: ...${payment.utr_ref.slice(-4)}`);
    if (payment.cheque_no) parts.push(`Ch #${payment.cheque_no}`);
    if (payment.card_last4) parts.push(`Card: ...${payment.card_last4}`);
    if (payment.payer_acct_last4) parts.push(`A/c: ...${payment.payer_acct_last4}`);
    
    // Date
    if (payment.date) {
      const date = new Date(payment.date);
      parts.push(date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }));
    }
    
    // Metal/Old Gold specific (only if not showing itemized breakdown)
    if (payment.fine_wt && !payment.items) parts.push(`${parseFloat(payment.fine_wt).toFixed(2)}g fine`);
    if (payment.metal_type) parts.push(payment.metal_type);
    
    // Note
    if (payment.note) parts.push(payment.note);
    if (payment.reference) parts.push(payment.reference);
    
    return parts.join(' • ');
  };

  const getChequeStatusBadge = (status) => {
    const styles = {
      'Received': 'bg-blue-100 text-blue-800',
      'Cleared': 'bg-green-100 text-green-800',
      'Bounced': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const generateWhatsAppLink = (payment) => {
    if (!customerPhone) return null;
    
    let message = `Payment Confirmation\n\n`;
    message += `Mode: ${payment.mode}\n`;
    if (payment.amount) message += `Amount: ₹${parseFloat(payment.amount || 0).toLocaleString('en-IN')}\n`;
    if (payment.fine_grams) message += `Fine Gold: ${payment.fine_grams}g\n`;
    if (payment.fine_wt) message += `Metal Credit: ${payment.fine_wt}g\n`;
    message += `Date: ${new Date(payment.date).toLocaleDateString('en-IN')}\n`;
    
    if (payment.upi_ref) message += `UPI Ref: ${payment.upi_ref}\n`;
    if (payment.utr_ref) message += `UTR: ${payment.utr_ref}\n`;
    if (payment.cheque_no) message += `Cheque No: ${payment.cheque_no}\n`;
    
    message += `\nThank you!`;
    
    return `https://wa.me/${customerPhone}?text=${encodeURIComponent(message)}`;
  };

  if (payments.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500 text-sm">
        No payments added yet
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary Badges */}
      <div className="flex flex-wrap gap-2">
        {totalCash > 0 && (
          <div className="px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
            Payments: ₹{totalCash.toLocaleString('en-IN')}
          </div>
        )}
        {totalOldGold > 0 && (
          <div className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
            Old Gold: {totalOldGoldFine.toFixed(2)}g • ₹{totalOldGold.toLocaleString('en-IN')}
          </div>
        )}
        {totalMetalCredit > 0 && (
          <div className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
            Metal Credit: {totalMetalCredit.toFixed(2)}g
          </div>
        )}
      </div>

      {/* Payment Entries */}
      <div className="space-y-2">
        {payments.map((payment, idx) => {
          const isRefund = payment.transaction_type === 'REFUND';
          return (
          <div 
            key={payment.payment_id || idx} 
            className={`border-2 rounded-lg p-3 ${isRefund ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-white'}`}
          >
            <div className="flex items-start justify-between mb-1">
              <div className="flex items-center gap-2 flex-1">
                <span className="text-xl">{isRefund ? '↩️' : getModeIcon(payment.mode)}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold text-sm ${isRefund ? 'text-red-700' : 'text-gray-800'}`}>
                      {isRefund ? 'REFUND - ' : ''}{payment.mode}
                    </span>
                    {payment.cheque_status && getChequeStatusBadge(payment.cheque_status)}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5 break-words">
                    {getPaymentDetails(payment)}
                  </div>
                </div>
              </div>
              <div className="text-right flex flex-col items-end gap-1">
                {payment.amount != null && (
                  <span className={`font-bold ${isRefund ? 'text-red-700' : 'text-amber-700'}`}>
                    {isRefund ? '-' : ''}₹{parseFloat(payment.amount || 0).toLocaleString('en-IN')}
                  </span>
                )}
                {(payment.fine_grams || payment.fine_wt) && (
                  <span className="text-xs font-semibold text-blue-700">
                    {parseFloat(payment.fine_grams || payment.fine_wt || 0).toFixed(2)}g
                  </span>
                )}
              </div>
            </div>
            
            {/* Old Gold Itemized Breakdown */}
            {payment.mode === 'Old Gold' && payment.items && payment.items.length > 0 && (
              <div className="mt-2 pt-2 border-t border-amber-200 space-y-1">
                <div className="text-xs font-semibold text-amber-800 mb-1">Items:</div>
                {payment.items.map((item, itemIdx) => (
                  <div key={itemIdx} className="bg-amber-50 rounded px-2 py-1.5 text-xs">
                    <div className="font-semibold text-gray-800">{item.item_name || `Item ${itemIdx + 1}`}</div>
                    <div className="text-gray-600 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      <span>Gross: {parseFloat(item.gross_wt || 0).toFixed(3)}g</span>
                      <span>Net: {parseFloat(item.net_wt || 0).toFixed(3)}g</span>
                      <span>Purity: {parseFloat(item.purity || 0).toFixed(1)}%</span>
                    </div>
                    <div className="text-gray-700 flex justify-between mt-0.5">
                      <span>Fine: {parseFloat(item.fine_wt || 0).toFixed(3)}g</span>
                      <span className="font-semibold">@ ₹{parseFloat(item.purchase_rate || 0).toFixed(0)}/g = ₹{parseFloat(item.line_total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Metal Credit Itemized Breakdown */}
            {payment.mode === 'Metal Credit' && payment.deposits && payment.deposits.length > 0 && (
              <div className="mt-2 pt-2 border-t border-blue-200 space-y-1">
                <div className="text-xs font-semibold text-blue-800 mb-1">Deposits:</div>
                {payment.deposits.map((deposit, depIdx) => {
                  const typeName = deposit.type === 'other' ? deposit.type_name_other : 
                                   deposit.type === 'cadbury' ? 'Cadbury' :
                                   deposit.type === 'rava' ? 'Rava' :
                                   deposit.type === 'old-gold' ? 'Old Gold (Jewellery)' :
                                   deposit.type === 'akj-new' ? 'AKJ New' : deposit.type;
                  
                  // Special format for AKJ New
                  if (deposit.type === 'akj-new') {
                    return (
                      <div key={depIdx} className="bg-blue-50 rounded px-2 py-1.5 text-xs">
                        <div className="font-semibold text-gray-800">
                          AKJ New — {deposit.item_name || 'Item'}
                        </div>
                        <div className="text-gray-600 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                          <span>Gross: {parseFloat(deposit.gross_wt || 0).toFixed(3)}g</span>
                          <span>Less: {parseFloat(deposit.less_wt || 0).toFixed(3)}g</span>
                          <span>Net: {parseFloat(deposit.net_wt || 0).toFixed(3)}g</span>
                          <span>Purity: {deposit.purity_label || `${parseFloat(deposit.purity || 0).toFixed(1)}%`}</span>
                        </div>
                        <div className="text-blue-700 flex justify-between mt-0.5">
                          <span className="font-semibold">Fine: {parseFloat(deposit.fine_wt || 0).toFixed(3)}g</span>
                        </div>
                        {/* Photos */}
                        {deposit.photos && deposit.photos.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {deposit.photos.map((photo, photoIdx) => (
                              <img 
                                key={photoIdx} 
                                src={photo} 
                                alt={`Photo ${photoIdx + 1}`} 
                                className="w-12 h-12 object-cover rounded border border-blue-200" 
                              />
                            ))}
                          </div>
                        )}
                        {/* Reference and Date */}
                        <div className="text-gray-500 text-xs mt-0.5">
                          {deposit.reference && <span>{deposit.reference} | </span>}
                          {deposit.date && new Date(deposit.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </div>
                      </div>
                    );
                  }
                  
                  // Standard format for other types
                  return (
                    <div key={depIdx} className="bg-blue-50 rounded px-2 py-1.5 text-xs">
                      <div className="font-semibold text-gray-800">
                        {typeName}
                        {deposit.item_name && ` (${deposit.item_name})`}
                      </div>
                      <div className="text-gray-600 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        {deposit.gross_wt && <span>Gross: {parseFloat(deposit.gross_wt).toFixed(3)}g</span>}
                        {deposit.less_wt && <span>Less: {parseFloat(deposit.less_wt).toFixed(3)}g</span>}
                        <span>Net: {parseFloat(deposit.net_wt || 0).toFixed(3)}g</span>
                        <span>Purity: {parseFloat(deposit.purity || 0).toFixed(1)}%</span>
                      </div>
                      <div className="text-blue-700 flex justify-between mt-0.5">
                        <span className="font-semibold">Fine: {parseFloat(deposit.fine_wt || 0).toFixed(3)}g</span>
                        {deposit.reference && <span className="text-gray-500 text-xs">{deposit.reference}</span>}
                      </div>
                    </div>
                  );
                })}
                {payment.advance_equivalent && (
                  <div className="text-xs text-blue-600 font-semibold mt-1">
                    Advance Equivalent: ₹{parseFloat(payment.advance_equivalent).toLocaleString('en-IN')}
                  </div>
                )}
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex gap-2 mt-2 pt-2 border-t border-gray-200">
              <button
                type="button"
                onClick={() => onEdit(payment, idx)}
                className="flex-1 py-1.5 px-2 bg-amber-100 text-amber-700 rounded text-xs font-semibold flex items-center justify-center gap-1"
              >
                <Edit3 className="w-3 h-3" />
                Edit
              </button>
              <button
                type="button"
                onClick={() => onDelete(idx)}
                className="flex-1 py-1.5 px-2 bg-red-100 text-red-700 rounded text-xs font-semibold flex items-center justify-center gap-1"
              >
                <Trash2 className="w-3 h-3" />
                Delete
              </button>
              {customerPhone && generateWhatsAppLink(payment) && (
                <a
                  href={generateWhatsAppLink(payment)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-1.5 px-2 bg-green-100 text-green-700 rounded text-xs font-semibold text-center"
                >
                  📱 WhatsApp
                </a>
              )}
            </div>
          </div>
        );
        })}
      </div>
    </div>
  );
};
